import { useCallback, useEffect, useMemo, useState } from 'react';

const ROLES = ['teacher', 'sponsor', 'coach', 'guardian', 'prospect', 'admin', 'superadmin'];

interface UserAllowlistControlsProps {
  email?: string;
  bearer?: Record<string, string>;
}

interface AllowlistEntry {
  role: string;
  active: boolean;
}

export default function UserAllowlistControls({ email, bearer }: UserAllowlistControlsProps) {
  const [entries, setEntries] = useState<AllowlistEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [pendingRole, setPendingRole] = useState<string | null>(null);
  const [error, setError] = useState('');

  const normalizedEmail = useMemo(() => (email || '').trim().toLowerCase(), [email]);

  const loadEntries = useCallback(async () => {
    if (!normalizedEmail) {
      setEntries([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/api/v1/admin/allowlist', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          ...(bearer || {}),
        },
        body: JSON.stringify({ action: 'list', email: normalizedEmail }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.error || `HTTP ${response.status}`);
      }
      const data = await response.json().catch(() => []);
      setEntries(Array.isArray(data) ? data : []);
    } catch (loadError: unknown) {
      setEntries([]);
      setError(loadError instanceof Error ? loadError.message : 'Unable to load allowlist roles.');
    } finally {
      setLoading(false);
    }
  }, [normalizedEmail, bearer]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const isAllowed = useCallback(
    (role: string) => entries.some((entry) => entry.role === role && entry.active),
    [entries],
  );

  const handleToggle = async (role: string, enabled: boolean) => {
    if (!normalizedEmail) return;
    setPendingRole(role);
    setError('');
    try {
      const response = await fetch('/api/v1/admin/allowlist', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          ...(bearer || {}),
        },
        body: JSON.stringify({
          action: enabled ? 'grant' : 'revoke',
          email: normalizedEmail,
          role,
        }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.error || `HTTP ${response.status}`);
      }
      await loadEntries();
    } catch (toggleError: unknown) {
      setError(toggleError instanceof Error ? toggleError.message : 'Unable to update role.');
    } finally {
      setPendingRole(null);
    }
  };

  return (
    <div className="mt-4 space-y-3">
      <div className="space-y-2">
        {ROLES.map((role) => (
          <label key={role} className="flex items-center justify-between rounded-lg border border-sand/60 bg-white px-4 py-2 text-sm">
            <span className="capitalize text-cocoa">{role}</span>
            <input
              type="checkbox"
              checked={isAllowed(role)}
              disabled={loading || pendingRole === role}
              onChange={(event) => handleToggle(role, event.target.checked)}
              className="h-4 w-4"
            />
          </label>
        ))}
      </div>
      {loading ? <p className="text-xs text-cocoa/60">Loading roles…</p> : null}
      {error ? <p className="text-xs text-terracotta">{error}</p> : null}
    </div>
  );
}
